(() => {
  const contentSelectors = ['.article-template__content', '.product__description'];
  const youtubePattern =
    /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube(?:-nocookie)?\.com\/(?:watch\?[^<>\s"']*|embed\/[A-Za-z0-9_-]{6,}|shorts\/[A-Za-z0-9_-]{6,})|youtu\.be\/[A-Za-z0-9_-]{6,})[^\s<>"']*/i;

  const getYoutubeId = (value) => {
    if (!value) return '';

    const match = String(value).match(youtubePattern);
    if (!match) return '';

    const rawUrl = match[0].startsWith('http') ? match[0] : `https://${match[0]}`;

    try {
      const url = new URL(rawUrl);
      const hostname = url.hostname.replace(/^www\.|^m\./, '');

      if (hostname === 'youtu.be') {
        return url.pathname.split('/').filter(Boolean)[0] || '';
      }

      if (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com') {
        if (url.pathname === '/watch') return url.searchParams.get('v') || '';

        const parts = url.pathname.split('/').filter(Boolean);
        if (parts[0] === 'embed' || parts[0] === 'shorts') return parts[1] || '';
      }
    } catch (error) {
      return match[1] || '';
    }

    const fallback = String(value).match(/(?:v=|embed\/|shorts\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
    return fallback ? fallback[1] : '';
  };

  const makeIframe = (videoId, title) => {
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    iframe.title = title || 'YouTube video';
    iframe.loading = 'lazy';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';
    iframe.allowFullscreen = true;
    iframe.setAttribute('allowfullscreen', '');
    return iframe;
  };

  const makeLiteEmbed = (videoId, title = '') => {
    const wrapper = document.createElement('div');
    wrapper.className = 'article-youtube-lite';
    wrapper.dataset.youtubeId = videoId;

    const button = document.createElement('button');
    button.className = 'article-youtube-lite__button';
    button.type = 'button';
    button.setAttribute('aria-label', title ? `Play YouTube video: ${title}` : 'Play YouTube video');

    const image = document.createElement('img');
    image.className = 'article-youtube-lite__poster';
    image.src = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
    image.alt = '';
    image.loading = 'lazy';
    image.addEventListener(
      'error',
      () => {
        image.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      },
      { once: true }
    );

    const icon = document.createElement('span');
    icon.className = 'article-youtube-lite__play';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<span></span>';

    button.append(image, icon);
    button.addEventListener('click', () => {
      wrapper.replaceChildren(makeIframe(videoId, title));
      wrapper.classList.add('is-playing');
    });

    wrapper.append(button);
    return wrapper;
  };

  const bindLiteEmbed = (wrapper) => {
    if (!wrapper || wrapper.dataset.youtubeBound === 'true') return;

    const videoId = wrapper.dataset.youtubeId;
    const button = wrapper.querySelector('.article-youtube-lite__button');
    const image = wrapper.querySelector('.article-youtube-lite__poster');
    const title = button?.getAttribute('aria-label')?.replace(/^Play YouTube video:\s*/i, '') || '';

    if (!videoId || !button) return;

    wrapper.dataset.youtubeBound = 'true';

    if (image) {
      image.addEventListener(
        'error',
        () => {
          image.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
        },
        { once: true }
      );
    }

    button.addEventListener('click', () => {
      wrapper.replaceChildren(makeIframe(videoId, title));
      wrapper.classList.add('is-playing');
    });
  };

  const replaceLegacyIframe = (iframe) => {
    const videoId = getYoutubeId(iframe.getAttribute('src'));
    if (!videoId) return;

    const title = iframe.getAttribute('title') || 'YouTube video';
    const player = makeLiteEmbed(videoId, title);
    const container = iframe.closest('.embed-container');

    if (container && container.querySelectorAll('iframe, object, embed').length === 1) {
      container.replaceWith(player);
      return;
    }

    iframe.replaceWith(player);
  };

  const isStandaloneElement = (element, urlText) => {
    const text = (element.textContent || '').trim();
    return text === urlText || text === urlText.replace(/^https?:\/\//, '');
  };

  const replaceStandaloneLink = (link) => {
    const href = link.getAttribute('href') || '';
    const videoId = getYoutubeId(href);
    if (!videoId) return;

    const block = link.closest('p, div, li');
    const title = (link.textContent || '').trim();

    if (block && isStandaloneElement(block, title || href)) {
      block.replaceWith(makeLiteEmbed(videoId, title));
      return;
    }

    if (isStandaloneElement(link, title || href)) {
      link.replaceWith(makeLiteEmbed(videoId, title));
    }
  };

  const replaceStandaloneTextUrl = (textNode) => {
    const text = textNode.nodeValue || '';
    const match = text.match(youtubePattern);
    if (!match) return;

    const urlText = match[0].trim();
    const videoId = getYoutubeId(urlText);
    if (!videoId) return;

    const parent = textNode.parentElement;
    if (!parent) return;

    const block = parent.closest('p, div, li');
    if (block && isStandaloneElement(block, text.trim())) {
      block.replaceWith(makeLiteEmbed(videoId, urlText));
    }
  };

  const convertContentVideos = (content) => {
    content.querySelectorAll('.article-youtube-lite[data-youtube-id]').forEach(bindLiteEmbed);

    content
      .querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtube-nocookie.com"], iframe[src*="youtu.be"]')
      .forEach(replaceLegacyIframe);

    content
      .querySelectorAll('a[href*="youtube.com"], a[href*="youtube-nocookie.com"], a[href*="youtu.be"]')
      .forEach(replaceStandaloneLink);

    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, textarea, .article-youtube-lite')) {
          return NodeFilter.FILTER_REJECT;
        }

        return youtubePattern.test(node.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(replaceStandaloneTextUrl);
  };

  const convertVideos = (root = document) => {
    const contents = new Set();

    contentSelectors.forEach((selector) => {
      if (root.matches?.(selector)) contents.add(root);
      root.querySelectorAll?.(selector).forEach((content) => contents.add(content));
    });

    contents.forEach(convertContentVideos);
  };

  document.addEventListener('DOMContentLoaded', () => convertVideos(document));
  document.addEventListener('shopify:section:load', (event) => convertVideos(event.target));
})();
